/**
 * Product Adder — pickup lead time
 * Reston Farm Garden Market
 *
 * The problem this solves: the bakery needs time to get an order out, but the
 * schedule picker offered every slot on the selected day — including windows
 * already in progress. Orders arrived for pickups the kitchen could not hit.
 *
 * What it does: greys out any of TODAY's pickup slots starting sooner than the
 * lead time, and greys out the Today tile once nothing is left. Future days are
 * never restricted.
 *
 * Contract with product-adder.js:
 *   - product-adder.js owns selection state and enables the time tiles when a
 *     date is chosen.
 *   - This module owns the 'adder-time-tile--past' / 'adder-date-tile--past'
 *     classes and re-disables too-soon tiles after that handler has run. Its
 *     listeners sit on the containers, so they fire on bubble AFTER the
 *     per-tile handlers inside product-adder.js.
 *   - When a held selection goes stale it re-clicks the selected date tile,
 *     which makes product-adder.js reset its own time selection and re-lock
 *     the add-to-cart button.
 *
 * Config is read from data attributes on [data-section="times"], rendered by
 * snippets/adder-type-option-schedule.liquid.
 */
(function () {
  'use strict';

  var FALLBACK_LEAD_MINUTES = 60;
  var TICK_MS = 60000;

  var root = null;
  var leadMinutes = FALLBACK_LEAD_MINUTES;
  var shopOffsetMinutes = null;
  var bouncing = false;

  function init() {
    root = document.querySelector('.product-adder[data-adder-type="option_schedule"]');
    if (!root) return;

    var timeSection = root.querySelector('[data-section="times"]');
    if (!timeSection) return;

    var lead = parseInt(timeSection.dataset.leadMinutes, 10);
    if (!isNaN(lead) && lead >= 0) leadMinutes = lead;

    var offset = parseInt(timeSection.dataset.shopUtcOffset, 10);
    if (isNaN(offset)) return; /* No clock anchor — leave the picker untouched. */
    shopOffsetMinutes = offset;

    apply();

    var dates = root.querySelector('.adder-date-tiles');
    if (dates) dates.addEventListener('click', function () { apply(); });

    setInterval(apply, TICK_MS);

    var section = root.closest('.shopify-section');
    if (section) section.addEventListener('submit', guardSubmit, true);
  }

  /** Wall-clock time at the store, independent of the visitor's own timezone. */
  function shopNow() {
    var shifted = new Date(Date.now() + shopOffsetMinutes * 60000);
    return {
      iso: shifted.toISOString().slice(0, 10),
      minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes()
    };
  }

  /** "10am–11am" -> 600. Returns null when the label cannot be read. */
  function startMinutes(label) {
    if (!label) return null;
    var head = String(label).split(/[–—-]/)[0];
    var parts = head.match(/(\d{1,2})(?::(\d{2}))?\s*([ap])m/i);
    if (!parts) return null;
    var hour = parseInt(parts[1], 10) % 12;
    if (/p/i.test(parts[3])) hour += 12;
    return hour * 60 + (parts[2] ? parseInt(parts[2], 10) : 0);
  }

  function selectedDateIso() {
    var el = root.querySelector('.adder-date-tile--selected');
    return el ? el.dataset.date : null;
  }

  /** Unreadable labels stay bookable — never block a sale over a parse failure. */
  function isTooSoon(tile, cutoff) {
    var start = startMinutes(tile.dataset.time);
    return start !== null && start < cutoff;
  }

  function apply() {
    if (shopOffsetMinutes === null) return;

    var now = shopNow();
    var cutoff = now.minutes + leadMinutes;
    var viewingToday = selectedDateIso() === now.iso;
    var remainingToday = 0;
    var staleSelection = false;

    root.querySelectorAll('.adder-time-tile').forEach(function (tile) {
      var late = isTooSoon(tile, cutoff);
      if (!late) remainingToday += 1;

      var block = late && viewingToday;
      tile.classList.toggle('adder-time-tile--past', block);
      if (block) {
        tile.disabled = true;
        if (tile.classList.contains('adder-time-tile--selected')) staleSelection = true;
      }
    });

    /* Today once it is spent, plus any tile left over from a page rendered
       before midnight. */
    root.querySelectorAll('.adder-date-tile').forEach(function (tile) {
      var iso = tile.dataset.date;
      var dead = iso < now.iso || (iso === now.iso && remainingToday === 0);
      tile.classList.toggle('adder-date-tile--past', dead);
      tile.disabled = dead;
    });

    if (staleSelection && !bouncing) {
      var selected = root.querySelector('.adder-date-tile--selected');
      if (selected && !selected.disabled) {
        bouncing = true;
        selected.click();
        bouncing = false;
      }
    }
  }

  /** Backstop: a page held open past the cutoff must not post a stale slot. */
  function guardSubmit(event) {
    if (shopOffsetMinutes === null) return;
    if (!event.target.matches || !event.target.matches('product-form-component form')) return;

    var now = shopNow();
    if (selectedDateIso() !== now.iso) return;

    var selected = root.querySelector('.adder-time-tile--selected');
    if (selected && isTooSoon(selected, now.minutes + leadMinutes)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      apply();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
